'use client';

import React, { useState } from 'react';
import {
  Calendar,
  CalendarClock,
  Bell,
  ChevronRight,
  Info,
  Clock,
  AlertTriangle,
  Star,
  RefreshCw,
  XCircle,
  ChevronDown
} from 'lucide-react';
import { Switch } from '@/components/ui/Switch';

export interface WhatsAppMessageItem {
  id: string;
  category?: 'reminders' | 'updates' | 'cancellations' | 'reviews';
  title: string;
  subtitle: string;
  trigger?: string;
  enabled: boolean;
  content: string;
}

interface WhatsAppMessagesListProps {
  messages: WhatsAppMessageItem[];
  onToggleMessage: (id: string) => void;
  onPreviewMessage: (message: WhatsAppMessageItem) => void;
  onEditMessage?: (message: WhatsAppMessageItem) => void;
}

export function WhatsAppMessagesList({
  messages,
  onToggleMessage,
  onPreviewMessage,
  onEditMessage,
}: WhatsAppMessagesListProps) {
  const [openCategories, setOpenCategories] = useState<{ [key: string]: boolean }>({
    reminders: true,
    updates: true,
    cancellations: true,
    reviews: true,
  });

  const toggleCategory = (cat: string) => {
    setOpenCategories((prev) => ({ ...prev, [cat]: !prev[cat] }));
  };

  const categories = [
    {
      id: 'reminders',
      title: 'Recordatorios y Confirmaciones',
      description: 'Avisos preventivos automáticos previos a la cita',
      icon: Bell,
    },
    {
      id: 'updates',
      title: 'Modificaciones y Reagendamientos',
      description: 'Disparos cuando el salón o el cliente cambian el turno',
      icon: RefreshCw,
    },
    {
      id: 'cancellations',
      title: 'Cancelaciones y Expiraciones',
      description: 'Liberación de turnos y avisos por seña no abonada',
      icon: XCircle,
    },
    {
      id: 'reviews',
      title: 'Fidelización y Reseñas',
      description: 'Mensajes post-atención solicitando valoración en Google',
      icon: Star,
    },
  ];

  const getIconForMessage = (title: string) => {
    if (title.includes('Cancel') || title.includes('cancelad')) return XCircle;
    if (title.includes('editad') || title.includes('reagend')) return CalendarClock;
    if (title.includes('reseña') || title.includes('Reseña')) return Star;
    return Calendar;
  };

  const enabledCount = messages.filter((m) => m.enabled).length;

  return (
    <div className="space-y-6">
      {/* Banner de Información Ergonómico */}
      <div className="p-4 bg-white border border-slate-200/90 rounded-2xl shadow-2xs space-y-2 text-xs text-slate-600 leading-relaxed">
        <div className="flex items-center gap-2 font-bold text-slate-900">
          <Info className="w-4 h-4 text-[#6255F5] shrink-0" />
          <span>Plantillas Nativas de WhatsApp Automatizadas</span>
        </div>
        <p>
          Las notificaciones se despachan automáticamente desde tu propio número vinculado a través de Evolution API / Baileys. Podés personalizar el texto de cada plantilla, activar o desactivar disparadores y utilizar variables dinámicas como <code className="font-mono text-[#6255F5] bg-violet-50 px-1 py-0.5 rounded">{'{{cliente}}'}</code>, <code className="font-mono text-[#6255F5] bg-violet-50 px-1 py-0.5 rounded">{'{{servicio}}'}</code> o <code className="font-mono text-[#6255F5] bg-violet-50 px-1 py-0.5 rounded">{'{{fecha}}'}</code>.
        </p>
      </div>

      {/* Listado en Acordeones por Categoría */}
      <div className="space-y-4">
        {categories.map((cat) => {
          const catMessages = messages.filter(
            (m) => (m.category || 'reminders') === cat.id
          );
          if (catMessages.length === 0) return null;

          const isOpen = openCategories[cat.id];
          const CatIcon = cat.icon;

          return (
            <div
              key={cat.id}
              className="bg-white border border-slate-200/90 rounded-2xl shadow-xs overflow-hidden"
            >
              {/* Header de Categoría */}
              <button
                type="button"
                onClick={() => toggleCategory(cat.id)}
                className="w-full px-5 py-3.5 bg-slate-50/80 hover:bg-slate-50 flex items-center justify-between text-left transition cursor-pointer border-b border-slate-100"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl bg-violet-50 text-[#6255F5] flex items-center justify-center">
                    <CatIcon className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-xs font-bold text-slate-900">
                      {cat.title}
                    </h3>
                    <p className="text-[10px] text-slate-500">
                      {cat.description}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <span className="text-[11px] font-semibold text-slate-500">
                    {catMessages.filter((m) => m.enabled).length} de {catMessages.length} activas
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 text-slate-400 transition-transform ${
                      isOpen ? 'transform rotate-180' : ''
                    }`}
                  />
                </div>
              </button>

              {/* Mensajes dentro del acordeón */}
              {isOpen && (
                <div className="divide-y divide-slate-100">
                  {catMessages.map((msg) => {
                    const MessageIcon = getIconForMessage(msg.title);

                    return (
                      <div
                        key={msg.id}
                        onClick={() => onEditMessage?.(msg)}
                        className="px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-slate-50/60 transition cursor-pointer group"
                      >
                        {/* Izquierda: Ícono + Título + Subtítulo */}
                        <div className="flex items-center gap-3.5 min-w-0">
                          <div className="w-9 h-9 rounded-xl bg-slate-50 text-slate-600 border border-slate-200 flex items-center justify-center shrink-0 group-hover:border-[#6255F5]/40 group-hover:text-[#6255F5] transition">
                            <MessageIcon className="w-4 h-4" />
                          </div>

                          <div className="min-w-0">
                            <h4 className="text-xs font-bold text-slate-900 group-hover:text-[#6255F5] transition truncate">
                              {msg.title}
                            </h4>
                            <p className="text-[11px] text-slate-500 truncate mt-0.5">
                              {msg.subtitle}
                            </p>
                          </div>
                        </div>

                        {/* Derecha: Badge Disparador, Botón Editar, Switch */}
                        <div
                          className="flex items-center gap-3 self-end sm:self-center shrink-0"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {msg.trigger && (
                            <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-lg text-[10px] font-bold">
                              {msg.trigger}
                            </span>
                          )}

                          <button
                            type="button"
                            onClick={() => onEditMessage?.(msg)}
                            className="text-xs font-bold text-[#6255F5] hover:underline flex items-center gap-0.5 cursor-pointer"
                          >
                            <span>Editar</span>
                            <ChevronRight className="w-3.5 h-3.5" />
                          </button>

                          <div className="pl-1">
                            <Switch
                              checked={msg.enabled}
                              onChange={() => onToggleMessage(msg.id)}
                              label={`Alternar ${msg.title}`}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
